import { gql } from 'apollo-boost'
import * as Fragments from './fragments'

export const SIGN_IN = gql`
  mutation signIn($email: String!, $password: String!, $remember: Boolean, $organizationId: ID) {
    signIn(email: $email, password: $password, remember: $remember, organizationId: $organizationId) {
      token
      requiresOrgSelection
      organizations {
        id
        name
        rights
      }
    }
  }
`

export const SWITCH_ORGANIZATION = gql`
  mutation switchOrganization($organizationId: ID!) {
    switchOrganization(organizationId: $organizationId) {
      token
      user {
        id
        name
        email
        rights
      }
    }
  }
`
export const SIGN_UP = gql`
  mutation signUp($email: String!, $name: String!, $organizationName: String!, $language: String!, $turnstileToken: String!) {
    signUp(email: $email, name: $name, organizationName: $organizationName, language: $language, turnstileToken: $turnstileToken) {
      id
    }
  }
`

export const UPDATE_USER_PASSWORD = gql`
  mutation updateUserPassword($id: ID!, $password: String) {
    updateUserPassword(id: $id, password: $password) {
      ...UserFragment
    }
  }
  ${Fragments.UserFragment}
`

export const SEND_USER_PASSWORD_RESET_LINK = gql`
  mutation sendResetUserPasswordLink($email: String!, $language: String!) {
    sendResetUserPasswordLink(email: $email, language: $language) {
      ...UserFragment
    }
  }
  ${Fragments.UserFragment}
`

export const RESET_USER_PASSWORD = gql`
  mutation resetUserPassword($id: ID!, $password: String, $language: String!) {
    resetUserPassword(id: $id, password: $password, language: $language) {
      ...UserFragment
    }
  }
  ${Fragments.UserFragment}
`

export const CREATE_USER = gql`
  mutation createUser($email: String!, $name: String!, $language: String!, $rights: AccessRights, $company: String) {
    createUser(email: $email, name: $name, language: $language, rights: $rights, company: $company) {
      id
      name
      email
      rights
    }
  }
`

export const CREATE_MANY_USERS = gql`
  mutation createManyUsers($users: [Json!]!, $language: String) {
    createManyUsers(users: $users, language: $language) {
      id
      name
      email
    }
  }
`

export const DELETE_USER = gql`
  mutation deleteUser($id: ID!) {
    deleteUser(id: $id) {
      id
    }
  }
`

// Offboarding in the UI is deactivation, not deletion — reversible, and it keeps
// the document provenance that a hard delete cannot give up. DELETE_USER stays
// for API-only use (freshly miscreated accounts).
export const DEACTIVATE_USER = gql`
  mutation deactivateUser($id: ID!) {
    deactivateUser(id: $id) {
      id
      deactivatedAt
    }
  }
`

export const REACTIVATE_USER = gql`
  mutation reactivateUser($id: ID!) {
    reactivateUser(id: $id) {
      id
      deactivatedAt
    }
  }
`
export const UPDATE_USER_RIGHTS = gql`
  mutation updateUserRights($id: ID!, $rights: AccessRights!) {
    updateUserRights(id: $id, rights: $rights) {
      ...UserFragment
    }
  }
  ${Fragments.UserFragment}
`

export const UPDATE_USER = gql`
  mutation updateUser($id: ID, $name: String, $email: String, $projectId: ID, $company: String) {
    updateUser(id: $id, name: $name, email: $email, projectId: $projectId, company: $company) {
      ...UserFragment
      company
      currentProject {
        id
        name
      }
    }
  }
  ${Fragments.UserFragment}
`

export const UPDATE_TENDER = gql`
  mutation updateTender(
    $id: ID!
    $name: String!
    $round: Int!
    $description: String!
    $address: Json
    $packageManagerId: ID!
    $commencementDate: DateTime!
    $submissionDate: DateTime!
    $hardcopyRequired: Boolean!
    $hardcopyAddress: Json
    $bidders: [ID!]!
    $priceBreakdown: [Json!]!
    $notifyBiddersOnFiles: Boolean
    $skipLog: Boolean
  ) {
    updateTender(
      id: $id
      name: $name
      description: $description
      address: $address
      packageManagerId: $packageManagerId
      commencementDate: $commencementDate
      submissionDate: $submissionDate
      hardcopyRequired: $hardcopyRequired
      hardcopyAddress: $hardcopyAddress
      bidders: $bidders
      priceBreakdown: $priceBreakdown
      notifyBiddersOnFiles: $notifyBiddersOnFiles
      skipLog: $skipLog
    ) {
      ...TenderFragment
    }
    updatePriceBreakdown(tenderId: $id, tenderRound: $round, priceBreakdown: $priceBreakdown) {
      id
    }
  }
  ${Fragments.TenderFragment}
`

export const CREATE_TENDER = gql`
  mutation createTender(
    $name: String!
    $description: String!
    $address: Json
    $packageManagerId: ID!
    $projectId: ID!
    $commencementDate: DateTime!
    $submissionDate: DateTime!
    $hardcopyRequired: Boolean
    $hardcopyAddress: Json
    $bidders: [ID!]!
  ) {
    createTender(
      name: $name
      description: $description
      address: $address
      projectId: $projectId
      packageManagerId: $packageManagerId
      commencementDate: $commencementDate
      submissionDate: $submissionDate
      hardcopyRequired: $hardcopyRequired
      hardcopyAddress: $hardcopyAddress
      bidders: $bidders
    ) {
      ...TenderFragment
    }
  }
  ${Fragments.TenderFragment}
`

export const SET_TENDER_FILES = gql`
  mutation ($id: ID!, $files: [ID!]!) {
    setTenderFiles(id: $id, files: $files) {
      ...TenderFragment
    }
  }
  ${Fragments.TenderFragment}
`

export const SET_BID_FILES = gql`
  mutation ($id: ID!, $files: [ID!]!) {
    setBidFiles(id: $id, files: $files) {
      ...BidFragment
    }
  }
  ${Fragments.BidFragment}
`

export const DELETE_TENDER = gql`
  mutation deleteTender($id: ID!) {
    deleteTender(id: $id) {
      id
    }
  }
`

export const TENDER_ACTION = gql`
  mutation tenderAction($id: ID!, $action: String!, $payload: Json) {
    tenderAction(id: $id, action: $action, payload: $payload) {
      ...TenderFragment
    }
  }
  ${Fragments.TenderFragment}
`

export const TENDER_MESSAGE = gql`
  mutation tenderMessage($tenderId: ID!, $message: String!, $parentId: ID, $isPublic: Boolean) {
    tenderMessage(
      tenderId: $tenderId
      message: $message
      parentId: $parentId
      isPublic: $isPublic
    ) {
      ...LogFragment
    }
  }
  ${Fragments.LogFragment}
`

export const UPDATE_LOG_MESSAGE = gql`
  mutation updateLogMessage($id: ID!, $message: String!, $isPublic: Boolean!) {
    updateLogMessage(id: $id, message: $message, isPublic: $isPublic) {
      ...LogFragment
    }
  }
  ${Fragments.LogFragment}
`

export const TENDER_BIDDER_MESSAGE = gql`
  mutation tenderBidderMessage($tenderId: ID!, $bidderId: ID!, $message: String!) {
    tenderBidderMessage(tenderId: $tenderId, bidderId: $bidderId, message: $message) {
      ...LogFragment
    }
  }
  ${Fragments.LogFragment}
`

export const CREATE_BIDDERS = gql`
  mutation createBidders($bidders: [Json!]!) {
    createBidders(bidders: $bidders) {
      id
      name
      tags
      phone
      contactName
      email
    }
  }
`

export const UPDATE_BIDDER = gql`
  mutation updateBidder(
    $id: ID!
    $name: String
    $contactName: String
    $email: String
    $tags: Json
    $phone: String!
    $address: Json
  ) {
    updateBidder(
      id: $id
      name: $name
      contactName: $contactName
      email: $email
      tags: $tags
      phone: $phone
      address: $address
    ) {
      id
      name
      contactName
      email
      tags
      phone
      address
    }
  }
`

export const DELETE_BIDDERS = gql`
  mutation deleteBidders($bidders: [ID!]!) {
    deleteBidders(bidders: $bidders) {
      id
      name
      tags
      phone
      contactName
      email
    }
  }
`

export const CREATE_FILE = gql`
  mutation ($input: CreateFileInput!) {
    createFile(input: $input) {
      file {
        id
      }
      uploadUrl
    }
  }
`
export const SET_FILE_UPLOADED = gql`
  mutation ($ids: [ID!]!) {
    setFileUploaded(ids: $ids) {
      ...FileFragment
    }
  }
  ${Fragments.FileFragment}
`

// export const UPLOAD_FILE = gql`
//   mutation uploadFile($file: Upload!, $size: Int!, $directory: String!) {
//     uploadFile(file: $file, size: $size, directory: $directory) {
//       ...FileFragment
//     }
//   }
//   ${Fragments.FileFragment}
// `

export const DELETE_FILE = gql`
  mutation deleteFile($ids: [ID!]!) {
    deleteFile(ids: $ids) {
      id
    }
  }
`

export const UPDATE_ORGANIZATION = gql`
  mutation updateOrganization($id: ID!, $name: String, $description: String, $address: Json) {
    updateOrganization(id: $id, name: $name, description: $description, address: $address) {
      id
      name
      description
      address
    }
  }
`

export const CREATE_PROJECT = gql`
  mutation createProject($name: String!, $description: String, $language: String, $address: Json) {
    createProject(name: $name, description: $description, language: $language, address: $address) {
      id
      name
      language
      description
      address
    }
  }
`

export const UPDATE_PROJECT = gql`
  mutation updateProject(
    $id: ID!
    $name: String!
    $code: String
    $description: String
    $language: String
    $address: Json
  ) {
    updateProject(
      id: $id
      name: $name
      code: $code
      description: $description
      language: $language
      address: $address
    ) {
      id
      name
      code
      language
      description
      address
    }
  }
`

export const UPDATE_TENDER_NOTES = gql`
  mutation ($id: ID!, $notes: Json) {
    updateTenderNotes(id: $id, notes: $notes) {
      id
      notes
    }
  }
`

export const DELETE_PROJECT = gql`
  mutation deleteProject($userId: ID!, $projectId: ID!) {
    deleteProject(userId: $userId, projectId: $projectId) {
      id
    }
  }
`

export const ADD_PROJECT_USERS = gql`
  mutation addProjectUsers($id: ID!, $users: [Json!]!) {
    addProjectUsers(id: $id, users: $users) {
      id
      users {
        id
        role
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const REMOVE_PROJECT_USERS = gql`
  mutation removeProjectUsers($id: ID!, $users: [ID!]!) {
    removeProjectUsers(id: $id, users: $users) {
      id
      users {
        id
        role
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const UPDATE_USER_PROJECT_ROLE = gql`
  mutation updateUserProjectRole($id: ID!, $role: Role!) {
    updateUserProjectRole(id: $id, role: $role) {
      id
      role
      user {
        id
        name
        email
      }
    }
  }
`

export const NOTIFY_BIDDER_TENDER_RUNNING = gql`
  mutation ($tenderId: ID!, $bidderId: ID!) {
    notifyBidderRunningTender(tenderId: $tenderId, bidderId: $bidderId)
  }
`

export const BIDDER_SUBMIT_BID = gql`
  mutation submitBid($data: Json!, $totalPrice: Int) {
    submitBid(data: $data, totalPrice: $totalPrice) {
      id
    }
  }
`

export const CREATE_SUBSCRIPTION = gql`
  mutation createSubscription($priceId: String!) {
    createSubscription(priceId: $priceId) {
      checkoutSessionId
    }
  }
`

// ==================== BUILDINGS ====================

export const CREATE_BUILDING = gql`
  mutation createBuilding(
    $projectId: ID!
    $name: String!
    $code: String
    $description: String
    $sortOrder: Int
  ) {
    createBuilding(
      projectId: $projectId
      name: $name
      code: $code
      description: $description
      sortOrder: $sortOrder
    ) {
      ...BuildingFragment
    }
  }
  ${Fragments.BuildingFragment}
`

export const UPDATE_BUILDING = gql`
  mutation updateBuilding(
    $id: ID!
    $name: String
    $code: String
    $description: String
    $sortOrder: Int
  ) {
    updateBuilding(
      id: $id
      name: $name
      code: $code
      description: $description
      sortOrder: $sortOrder
    ) {
      ...BuildingFragment
    }
  }
  ${Fragments.BuildingFragment}
`

export const DELETE_BUILDING = gql`
  mutation deleteBuilding($id: ID!) {
    deleteBuilding(id: $id) {
      id
    }
  }
`

// ==================== FLOORS ====================

export const CREATE_FLOOR = gql`
  mutation createFloor(
    $buildingId: ID!
    $name: String!
    $level: Int!
    $description: String
    $sortOrder: Int
  ) {
    createFloor(
      buildingId: $buildingId
      name: $name
      level: $level
      description: $description
      sortOrder: $sortOrder
    ) {
      ...FloorFragment
    }
  }
  ${Fragments.FloorFragment}
`

export const UPDATE_FLOOR = gql`
  mutation updateFloor(
    $id: ID!
    $name: String
    $level: Int
    $description: String
    $sortOrder: Int
  ) {
    updateFloor(
      id: $id
      name: $name
      level: $level
      description: $description
      sortOrder: $sortOrder
    ) {
      ...FloorFragment
    }
  }
  ${Fragments.FloorFragment}
`

export const DELETE_FLOOR = gql`
  mutation deleteFloor($id: ID!) {
    deleteFloor(id: $id) {
      id
    }
  }
`

export const SET_FLOOR_BLUEPRINT = gql`
  mutation setFloorBlueprint($floorId: ID!, $fileId: ID) {
    setFloorBlueprint(floorId: $floorId, fileId: $fileId) {
      ...FloorFragment
    }
  }
  ${Fragments.FloorFragment}
`

export const PROCESS_BLUEPRINT_DZI = gql`
  mutation processBlueprintDzi($fileId: ID!) {
    processBlueprintDzi(fileId: $fileId) {
      id
      type
      status
      progress
      currentStep
      totalSteps
      currentStepNum
      error
      createdAt
    }
  }
`

// ==================== SNAGS ====================

export const CREATE_SNAG = gql`
  mutation createSnag($input: CreateSnagInput!) {
    createSnag(input: $input) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const UPDATE_SNAG = gql`
  mutation updateSnag($id: ID!, $input: UpdateSnagInput!) {
    updateSnag(id: $id, input: $input) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const DELETE_SNAG = gql`
  mutation deleteSnag($id: ID!) {
    deleteSnag(id: $id) {
      id
    }
  }
`

export const UPDATE_SNAG_PIN = gql`
  mutation updateSnagPin($id: ID!, $pinX: Float!, $pinY: Float!) {
    updateSnagPin(id: $id, pinX: $pinX, pinY: $pinY) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const SET_SNAG_FILES = gql`
  mutation setSnagFiles($id: ID!, $fileIds: [ID!]!) {
    setSnagFiles(id: $id, fileIds: $fileIds) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const RESOLVE_SNAG = gql`
  mutation resolveSnag($id: ID!) {
    resolveSnag(id: $id) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const VERIFY_SNAG = gql`
  mutation verifySnag($id: ID!) {
    verifySnag(id: $id) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const CLOSE_SNAG = gql`
  mutation closeSnag($id: ID!) {
    closeSnag(id: $id) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const REOPEN_SNAG = gql`
  mutation reopenSnag($id: ID!) {
    reopenSnag(id: $id) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const BULK_UPDATE_SNAGS = gql`
  mutation bulkUpdateSnags(
    $ids: [ID!]!
    $status: SnagStatus
    $priority: SnagPriority
    $assignedToId: ID
    $infrastructureType: String
  ) {
    bulkUpdateSnags(
      ids: $ids
      status: $status
      priority: $priority
      assignedToId: $assignedToId
      infrastructureType: $infrastructureType
    ) {
      ...SnagFragment
    }
  }
  ${Fragments.SnagFragment}
`

export const BULK_DELETE_SNAGS = gql`
  mutation bulkDeleteSnags($ids: [ID!]!) {
    bulkDeleteSnags(ids: $ids) {
      id
    }
  }
`

export const LOG_SNAG_ACTIVITY = gql`
  mutation logSnagActivity($snagId: ID!, $title: String!, $description: String) {
    logSnagActivity(snagId: $snagId, title: $title, description: $description) {
      id
      title
      description
      user {
        id
        name
      }
      createdAt
    }
  }
`

// ==================== SNAG SETTINGS ====================

export const UPDATE_SNAG_SETTINGS = gql`
  mutation updateSnagSettings(
    $projectId: ID!
    $infrastructureTypes: [String!]
    $defaultPriority: SnagPriority
  ) {
    updateSnagSettings(
      projectId: $projectId
      infrastructureTypes: $infrastructureTypes
      defaultPriority: $defaultPriority
    ) {
      id
      projectId
      infrastructureTypes
      defaultPriority
      createdAt
      updatedAt
    }
  }
`

// ==================== PDF EXPORT ====================

export const EXPORT_SNAGS_PDF = gql`
  mutation exportSnagsPdf($projectId: ID!, $filter: SnagFilterInput, $language: String) {
    exportSnagsPdf(projectId: $projectId, filter: $filter, language: $language) {
      id
      type
      status
      progress
      currentStep
      totalSteps
      currentStepNum
      error
    }
  }
`

// ============================================
// DOC CONTROL MUTATIONS
// ============================================

export const CREATE_DOC_FOLDER = gql`
  mutation createDocFolder($input: DocFolderCreateInput!) {
    createDocFolder(input: $input) {
      id
      name
      description
      parentId
      organizationId
      createdAt
    }
  }
`

export const UPDATE_DOC_FOLDER = gql`
  mutation updateDocFolder($id: ID!, $input: DocFolderUpdateInput!) {
    updateDocFolder(id: $id, input: $input) {
      id
      name
      description
    }
  }
`

export const DELETE_DOC_FOLDER = gql`
  mutation deleteDocFolder($id: ID!) {
    deleteDocFolder(id: $id) {
      id
    }
  }
`

export const MOVE_DOC_FOLDER = gql`
  mutation moveDocFolder($id: ID!, $newParentId: ID) {
    moveDocFolder(id: $id, newParentId: $newParentId) {
      id
      parentId
    }
  }
`

export const CREATE_DOC_FOLDER_TREE = gql`
  mutation createDocFolderTree($rootFolderId: ID!, $folders: [DocFolderTreeNodeInput!]!) {
    createDocFolderTree(rootFolderId: $rootFolderId, folders: $folders) {
      tempId
      folderId
    }
  }
`

export const CREATE_DOC_DOCUMENT = gql`
  mutation createDocDocument($input: DocDocumentCreateInput!) {
    createDocDocument(input: $input) {
      id
      name
      description
      folderId
    }
  }
`

export const UPDATE_DOC_DOCUMENT = gql`
  mutation updateDocDocument($id: ID!, $input: DocDocumentUpdateInput!) {
    updateDocDocument(id: $id, input: $input) {
      id
      name
      description
    }
  }
`

export const DELETE_DOC_DOCUMENT = gql`
  mutation deleteDocDocument($id: ID!) {
    deleteDocDocument(id: $id) {
      id
    }
  }
`


export const BULK_DELETE_DOC_DOCUMENTS = gql`
  mutation bulkDeleteDocDocuments($ids: [ID!]!) {
    bulkDeleteDocDocuments(ids: $ids) {
      id
    }
  }
`

export const MOVE_DOC_DOCUMENT = gql`
  mutation moveDocDocument($id: ID!, $newFolderId: ID!) {
    moveDocDocument(id: $id, newFolderId: $newFolderId) {
      id
      folderId
    }
  }
`

export const UPLOAD_DOC_REVISION = gql`
  mutation uploadDocRevision($fileId: ID!, $input: DocRevisionUploadInput!) {
    uploadDocRevision(fileId: $fileId, input: $input) {
      id
      documentId
      version
      uploadedAt
      comment
      document {
        id
        name
        currentRevisionId
      }
    }
  }
`

export const RESTORE_DOC_REVISION = gql`
  mutation restoreDocRevision($documentId: ID!, $revisionId: ID!) {
    restoreDocRevision(documentId: $documentId, revisionId: $revisionId) {
      id
      currentRevisionId
    }
  }
`

export const GENERATE_DOC_PREVIEW = gql`
  mutation generateDocPreview($revisionId: ID!) {
    generateDocPreview(revisionId: $revisionId) {
      id
      previewUrl
    }
  }
`

// Permission mutations
export const GRANT_DOC_FOLDER_PERMISSION = gql`
  mutation grantDocFolderPermission($folderId: ID!, $level: DocPermissionRole!, $userId: ID, $roleId: ID) {
    grantDocFolderPermission(folderId: $folderId, level: $level, userId: $userId, roleId: $roleId) {
      id
      folderId
      userId
      roleId
      level
    }
  }
`

export const REVOKE_DOC_FOLDER_PERMISSION = gql`
  mutation revokeDocFolderPermission($folderId: ID!, $userId: ID, $roleId: ID) {
    revokeDocFolderPermission(folderId: $folderId, userId: $userId, roleId: $roleId)
  }
`

export const SET_DOC_FOLDER_RESTRICTED = gql`
  mutation setDocFolderRestricted($folderId: ID!, $restricted: Boolean!) {
    setDocFolderRestricted(folderId: $folderId, restricted: $restricted) {
      id
      restricted
    }
  }
`

// Share link mutations
export const CREATE_DOC_SHARE_LINK = gql`
  mutation createDocShareLink(
    $folderId: ID
    $documentId: ID
    $password: String
    $expiresAt: DateTime
  ) {
    createDocShareLink(
      folderId: $folderId
      documentId: $documentId
      password: $password
      expiresAt: $expiresAt
    ) {
      id
      token
      url
      hasPassword
      expiresAt
      createdAt
    }
  }
`

export const REVOKE_DOC_SHARE_LINK = gql`
  mutation revokeDocShareLink($id: ID!) {
    revokeDocShareLink(id: $id)
  }
`

// Transmittal mutations
export const CREATE_DOC_TRANSMITTAL = gql`
  mutation createDocTransmittal($input: DocTransmittalCreateInput!) {
    createDocTransmittal(input: $input) {
      id
      name
      description
      reference
      status
      token
      url
    }
  }
`

export const UPDATE_DOC_TRANSMITTAL = gql`
  mutation updateDocTransmittal($id: ID!, $input: DocTransmittalCreateInput!) {
    updateDocTransmittal(id: $id, input: $input) {
      id
      name
      description
      reference
      expiresAt
    }
  }
`

export const DELETE_DOC_TRANSMITTAL = gql`
  mutation deleteDocTransmittal($id: ID!) {
    deleteDocTransmittal(id: $id)
  }
`

export const BULK_DELETE_DOC_TRANSMITTALS = gql`
  mutation bulkDeleteDocTransmittals($ids: [ID!]!) {
    bulkDeleteDocTransmittals(ids: $ids) {
      id
    }
  }
`

export const ADD_DOC_TRANSMITTAL_ITEMS = gql`
  mutation addDocTransmittalItems($transmittalId: ID!, $items: [DocTransmittalItemInput!]!) {
    addDocTransmittalItems(transmittalId: $transmittalId, items: $items) {
      id
      itemCount
      items {
        id
        document {
          id
          name
        }
        revision {
          id
          version
          downloadUrl
          file {
            id
            filename
            mimetype
            size
          }
        }
      }
    }
  }
`

export const REMOVE_DOC_TRANSMITTAL_ITEM = gql`
  mutation removeDocTransmittalItem($transmittalId: ID!, $documentId: ID!) {
    removeDocTransmittalItem(transmittalId: $transmittalId, documentId: $documentId) {
      id
      itemCount
      items {
        id
        document {
          id
          name
        }
        revision {
          id
          version
          downloadUrl
          file {
            id
            filename
            mimetype
            size
          }
        }
      }
    }
  }
`

export const ADD_DOC_TRANSMITTAL_RECIPIENTS = gql`
  mutation addDocTransmittalRecipients(
    $transmittalId: ID!
    $recipients: [DocTransmittalRecipientInput!]!
  ) {
    addDocTransmittalRecipients(transmittalId: $transmittalId, recipients: $recipients) {
      id
      recipientCount
      recipients {
        id
        userId
        email
        notifiedAt
        viewedAt
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const REMOVE_DOC_TRANSMITTAL_RECIPIENT = gql`
  mutation removeDocTransmittalRecipient($transmittalId: ID!, $recipientId: ID!) {
    removeDocTransmittalRecipient(transmittalId: $transmittalId, recipientId: $recipientId) {
      id
      recipientCount
      recipients {
        id
        userId
        email
        notifiedAt
        viewedAt
        user {
          id
          name
          email
        }
      }
    }
  }
`

export const SEND_DOC_TRANSMITTAL = gql`
  mutation sendDocTransmittal($id: ID!) {
    sendDocTransmittal(id: $id) {
      id
      status
      sentAt
      url
    }
  }
`

export const CREATE_AND_SEND_DOC_TRANSMITTAL = gql`
  mutation createAndSendDocTransmittal($input: CreateAndSendDocTransmittalInput!) {
    createAndSendDocTransmittal(input: $input) {
      id
      name
      status
      sentAt
      token
      url
      itemCount
      recipientCount
    }
  }
`

// Document linking mutations
export const LINK_DOCUMENT_TO_TENDER = gql`
  mutation linkDocumentToTender($tenderId: ID!, $documentId: ID!) {
    linkDocumentToTender(tenderId: $tenderId, documentId: $documentId) {
      id
      tenderId
      documentId
    }
  }
`

export const UNLINK_DOCUMENT_FROM_TENDER = gql`
  mutation unlinkDocumentFromTender($tenderId: ID!, $documentId: ID!) {
    unlinkDocumentFromTender(tenderId: $tenderId, documentId: $documentId)
  }
`

export const LINK_DOCUMENT_TO_SNAG = gql`
  mutation linkDocumentToSnag($snagId: ID!, $documentId: ID!) {
    linkDocumentToSnag(snagId: $snagId, documentId: $documentId) {
      id
      snagId
      documentId
    }
  }
`

export const UNLINK_DOCUMENT_FROM_SNAG = gql`
  mutation unlinkDocumentFromSnag($snagId: ID!, $documentId: ID!) {
    unlinkDocumentFromSnag(snagId: $snagId, documentId: $documentId)
  }
`

// ---- AI Evaluation ----

export const START_AI_TENDER_EXTRACTION = gql`
  mutation startAiTenderExtraction($tenderId: ID!) {
    startAiTenderExtraction(tenderId: $tenderId) {
      id status progress
    }
  }
`

export const START_AI_BID_EXTRACTION = gql`
  mutation startAiBidExtraction($tenderId: ID!, $bidId: ID!) {
    startAiBidExtraction(tenderId: $tenderId, bidId: $bidId) {
      id status progress
    }
  }
`

export const START_AI_COMPARISON = gql`
  mutation startAiComparison($tenderId: ID!) {
    startAiComparison(tenderId: $tenderId) {
      id status progress
    }
  }
`

export const GENERATE_AI_REPORT = gql`
  mutation generateAiReport($tenderId: ID!) {
    generateAiReport(tenderId: $tenderId) {
      id status progress
    }
  }
`

export const CANCEL_AI_JOB = gql`
  mutation cancelAiJob($jobId: ID!) {
    cancelAiJob(jobId: $jobId) {
      id status
    }
  }
`

export const APPROVE_AI_MAPPING = gql`
  mutation approveAiMapping($mappingId: ID!, $approved: Boolean!, $note: String) {
    approveAiMapping(mappingId: $mappingId, approved: $approved, note: $note) {
      id isApproved reviewNote
    }
  }
`

export const ANALYZE_EXCEL_FILE = gql`
  mutation analyzeExcelFile($tenderId: ID, $bidId: ID, $fileId: ID!) {
    analyzeExcelFile(tenderId: $tenderId, bidId: $bidId, fileId: $fileId) {
      id fileId status confidence warnings
      rawAnalysis sampleItems
    }
  }
`

export const UPDATE_EXTRACTION_PREVIEW = gql`
  mutation updateExtractionPreview($id: ID!, $overrides: Json!) {
    updateExtractionPreview(id: $id, overrides: $overrides) {
      id userOverrides
    }
  }
`

export const CONFIRM_AND_EXTRACT = gql`
  mutation confirmAndExtract($previewId: ID!) {
    confirmAndExtract(previewId: $previewId) {
      id type status progress
    }
  }
`

export const SUBMIT_EXTRACTION_CORRECTIONS = gql`
  mutation submitExtractionCorrections($tenderId: ID!, $bidId: ID, $corrections: [Json!]!) {
    submitExtractionCorrections(tenderId: $tenderId, bidId: $bidId, corrections: $corrections) {
      id type status progress
    }
  }
`

// ============================================
// MEETINGS
// ============================================

export const CREATE_MEETING = gql`
  mutation createMeeting($input: CreateMeetingInput!) {
    createMeeting(input: $input) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const UPDATE_MEETING = gql`
  mutation updateMeeting($id: ID!, $input: UpdateMeetingInput!) {
    updateMeeting(id: $id, input: $input) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const DELETE_MEETING = gql`
  mutation deleteMeeting($id: ID!) {
    deleteMeeting(id: $id) { id }
  }
`

export const DUPLICATE_MEETING = gql`
  mutation duplicateMeeting($id: ID!) {
    duplicateMeeting(id: $id) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const START_MEETING = gql`
  mutation startMeeting($id: ID!) {
    startMeeting(id: $id) { ...MeetingFullFragment }
  }
  ${Fragments.MeetingFullFragment}
`

export const COMPLETE_MEETING = gql`
  mutation completeMeeting($id: ID!) {
    completeMeeting(id: $id) { ...MeetingFullFragment }
  }
  ${Fragments.MeetingFullFragment}
`

export const CANCEL_MEETING = gql`
  mutation cancelMeeting($id: ID!) {
    cancelMeeting(id: $id) { ...MeetingFullFragment }
  }
  ${Fragments.MeetingFullFragment}
`

export const REOPEN_MEETING = gql`
  mutation reopenMeeting($id: ID!) {
    reopenMeeting(id: $id) { ...MeetingFullFragment }
  }
  ${Fragments.MeetingFullFragment}
`

export const FINALIZE_MEETING = gql`
  mutation finalizeMeeting($id: ID!, $minutesContent: String!) {
    finalizeMeeting(id: $id, minutesContent: $minutesContent) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const SET_MEETING_ATTENDEES = gql`
  mutation setMeetingAttendees($id: ID!, $attendeeIds: [ID!]!) {
    setMeetingAttendees(id: $id, attendeeIds: $attendeeIds) {
      id
      attendeeCount
      attendees { id name email }
    }
  }
`

export const SET_MEETING_EXTERNAL_GUESTS = gql`
  mutation setMeetingExternalGuests($id: ID!, $guests: [ExternalGuestInput!]!) {
    setMeetingExternalGuests(id: $id, guests: $guests) {
      id
      externalGuests {
        id
        name
        email
        company
        present
        orderIndex
      }
    }
  }
`

export const SET_ATTENDEE_PRESENCE = gql`
  mutation setAttendeePresence($meetingId: ID!, $userId: ID!, $present: Boolean!) {
    setAttendeePresence(meetingId: $meetingId, userId: $userId, present: $present) {
      id
      presentAttendeeIds
    }
  }
`

export const SET_EXTERNAL_GUEST_PRESENCE = gql`
  mutation setExternalGuestPresence($meetingId: ID!, $guestId: ID!, $present: Boolean!) {
    setExternalGuestPresence(meetingId: $meetingId, guestId: $guestId, present: $present) {
      id
      externalGuests {
        id
        name
        email
        company
        present
        orderIndex
      }
    }
  }
`

export const ADD_MEETING_ATTENDEE_PRESENT = gql`
  mutation addMeetingAttendeePresent($meetingId: ID!, $userId: ID!) {
    addMeetingAttendeePresent(meetingId: $meetingId, userId: $userId) {
      id
      presentAttendeeIds
      attendeeCount
      attendees { id name email }
    }
  }
`

export const ADD_MEETING_EXTERNAL_GUEST_PRESENT = gql`
  mutation addMeetingExternalGuestPresent($meetingId: ID!, $guest: ExternalGuestInput!) {
    addMeetingExternalGuestPresent(meetingId: $meetingId, guest: $guest) {
      id
      externalGuests {
        id
        name
        email
        company
        present
        orderIndex
      }
    }
  }
`

export const ADD_AGENDA_ITEM = gql`
  mutation addAgendaItem($meetingId: ID!, $input: CreateAgendaItemInput!) {
    addAgendaItem(meetingId: $meetingId, input: $input) {
      ...AgendaItemFragment
    }
  }
  ${Fragments.AgendaItemFragment}
`

export const UPDATE_AGENDA_ITEM = gql`
  mutation updateAgendaItem($id: ID!, $input: CreateAgendaItemInput!) {
    updateAgendaItem(id: $id, input: $input) {
      ...AgendaItemFragment
    }
  }
  ${Fragments.AgendaItemFragment}
`

export const REMOVE_AGENDA_ITEM = gql`
  mutation removeAgendaItem($id: ID!) {
    removeAgendaItem(id: $id) { id }
  }
`

export const REORDER_AGENDA_ITEMS = gql`
  mutation reorderAgendaItems($meetingId: ID!, $itemIds: [ID!]!) {
    reorderAgendaItems(meetingId: $meetingId, itemIds: $itemIds) {
      ...AgendaItemFragment
    }
  }
  ${Fragments.AgendaItemFragment}
`

export const ADD_AGENDA_SUB_ITEM = gql`
  mutation addAgendaSubItem($agendaItemId: ID!, $input: CreateAgendaSubItemInput!) {
    addAgendaSubItem(agendaItemId: $agendaItemId, input: $input) {
      ...AgendaSubItemFragment
    }
  }
  ${Fragments.AgendaSubItemFragment}
`

export const UPDATE_AGENDA_SUB_ITEM = gql`
  mutation updateAgendaSubItem($id: ID!, $input: UpdateAgendaSubItemInput!) {
    updateAgendaSubItem(id: $id, input: $input) {
      ...AgendaSubItemFragment
    }
  }
  ${Fragments.AgendaSubItemFragment}
`

export const REMOVE_AGENDA_SUB_ITEM = gql`
  mutation removeAgendaSubItem($id: ID!) {
    removeAgendaSubItem(id: $id) { id }
  }
`

export const REORDER_AGENDA_SUB_ITEMS = gql`
  mutation reorderAgendaSubItems($agendaItemId: ID!, $subItemIds: [ID!]!) {
    reorderAgendaSubItems(agendaItemId: $agendaItemId, subItemIds: $subItemIds) {
      ...AgendaSubItemFragment
    }
  }
  ${Fragments.AgendaSubItemFragment}
`

export const CREATE_ACTION_ITEM = gql`
  mutation createActionItem($input: CreateActionItemInput!) {
    createActionItem(input: $input) {
      ...ActionItemFragment
    }
  }
  ${Fragments.ActionItemFragment}
`

export const UPDATE_ACTION_ITEM = gql`
  mutation updateActionItem($id: ID!, $input: UpdateActionItemInput!) {
    updateActionItem(id: $id, input: $input) {
      ...ActionItemFragment
    }
  }
  ${Fragments.ActionItemFragment}
`

export const DELETE_ACTION_ITEM = gql`
  mutation deleteActionItem($id: ID!) {
    deleteActionItem(id: $id) { id }
  }
`

export const ADD_ACTION_ITEM_UPDATE = gql`
  mutation addActionItemUpdate($actionItemId: ID!, $body: String!) {
    addActionItemUpdate(actionItemId: $actionItemId, body: $body) {
      ...ActionItemUpdateFragment
    }
  }
  ${Fragments.ActionItemUpdateFragment}
`

export const REMOVE_ACTION_ITEM_UPDATE = gql`
  mutation removeActionItemUpdate($id: ID!) {
    removeActionItemUpdate(id: $id) { id }
  }
`

export const SET_MEETING_LINKS = gql`
  mutation setMeetingLinks($id: ID!, $snagIds: [ID!], $docIds: [ID!], $tenderIds: [ID!]) {
    setMeetingLinks(id: $id, snagIds: $snagIds, docIds: $docIds, tenderIds: $tenderIds) {
      id
      linkedSnags { id number title status }
      linkedDocs { id name }
      linkedTenders { id name }
    }
  }
`

export const SET_MEETING_FILES = gql`
  mutation setMeetingFiles($id: ID!, $fileIds: [ID!]!) {
    setMeetingFiles(id: $id, fileIds: $fileIds) {
      id
      files { id filename size key mimetype url }
    }
  }
`

export const SET_AGENDA_ITEM_FILES = gql`
  mutation setAgendaItemFiles($id: ID!, $fileIds: [ID!]!) {
    setAgendaItemFiles(id: $id, fileIds: $fileIds) {
      ...AgendaItemFragment
    }
  }
  ${Fragments.AgendaItemFragment}
`

export const SET_ACTION_ITEM_FILES = gql`
  mutation setActionItemFiles($id: ID!, $fileIds: [ID!]!) {
    setActionItemFiles(id: $id, fileIds: $fileIds) {
      ...ActionItemFragment
    }
  }
  ${Fragments.ActionItemFragment}
`

export const EXPORT_MEETING_PDF = gql`
  mutation exportMeetingPdf($id: ID!) {
    exportMeetingPdf(id: $id) {
      id
      type
      status
      progress
      error
    }
  }
`

export const DISTRIBUTE_MEETING = gql`
  mutation distributeMeeting($id: ID!, $extraEmails: [String!]) {
    distributeMeeting(id: $id, extraEmails: $extraEmails) {
      id
      distributedAt
    }
  }
`

export const SEND_MEETING_INVITATION = gql`
  mutation sendMeetingInvitation(
    $id: ID!
    $extraEmails: [String!]
    $customMessage: String
  ) {
    sendMeetingInvitation(
      id: $id
      extraEmails: $extraEmails
      customMessage: $customMessage
    ) {
      id
      invitationsSentAt
      invitationsSequence
    }
  }
`

export const SET_MEETING_RECURRENCE = gql`
  mutation setMeetingRecurrence(
    $id: ID!
    $recurrence: MeetingRecurrence!
    $recurrenceUntil: DateTime
  ) {
    setMeetingRecurrence(
      id: $id
      recurrence: $recurrence
      recurrenceUntil: $recurrenceUntil
    ) {
      id
      recurrence
      recurrenceUntil
      nextScheduledAt
    }
  }
`

export const FINALIZE_MEETING_OCCURRENCE = gql`
  mutation finalizeMeetingOccurrence($id: ID!, $scheduleNext: Boolean) {
    finalizeMeetingOccurrence(id: $id, scheduleNext: $scheduleNext) {
      ...MeetingFullFragment
    }
  }
  ${Fragments.MeetingFullFragment}
`

export const STOP_RECURRENCE = gql`
  mutation stopRecurrence($id: ID!) {
    stopRecurrence(id: $id) {
      id
      recurrence
    }
  }
`

export const PROMOTE_SUB_ITEM_TO_ACTION = gql`
  mutation promoteSubItemToActionItem($id: ID!) {
    promoteSubItemToActionItem(id: $id) {
      ...ActionItemFragment
    }
  }
  ${Fragments.ActionItemFragment}
`

export const DEMOTE_ACTION_TO_SUB_ITEM = gql`
  mutation demoteActionItemToSubItem($id: ID!, $kind: AgendaSubItemKind!) {
    demoteActionItemToSubItem(id: $id, kind: $kind) {
      ...AgendaSubItemFragment
    }
  }
  ${Fragments.AgendaSubItemFragment}
`

export const REORDER_AGENDA_ENTRIES = gql`
  mutation reorderAgendaEntries(
    $agendaItemId: ID!
    $entries: [AgendaEntryReorderInput!]!
  ) {
    reorderAgendaEntries(agendaItemId: $agendaItemId, entries: $entries) {
      id
      subItems {
        ...AgendaSubItemFragment
      }
      actionItems {
        ...ActionItemFragment
      }
    }
  }
  ${Fragments.AgendaSubItemFragment}
  ${Fragments.ActionItemFragment}
`

export const SET_USER_MODULE_LEVEL = gql`
  mutation SetUserModuleLevel($userId: ID!, $projectId: ID!, $module: String!, $level: ModuleAccessLevel!) {
    setUserModuleLevel(userId: $userId, projectId: $projectId, module: $module, level: $level) {
      id
      level
    }
  }
`

export const SET_USER_MODULE_ACTIONS = gql`
  mutation SetUserModuleActions($userId: ID!, $projectId: ID!, $module: String!, $actions: [String!]!) {
    setUserModuleActions(userId: $userId, projectId: $projectId, module: $module, actions: $actions) {
      id
      actions
    }
  }
`

export const CLEAR_USER_MODULE_ACCESS = gql`
  mutation ClearUserModuleAccess($userId: ID!, $projectId: ID!, $module: String!) {
    clearUserModuleAccess(userId: $userId, projectId: $projectId, module: $module)
  }
`

export const DISCONNECT_MICROSOFT_ACCOUNT = gql`
  mutation disconnectMicrosoftAccount {
    disconnectMicrosoftAccount {
      id
      microsoftConnected
      microsoftAccountEmail
    }
  }
`

// ==================== PROJECT ROLES ====================

export const SET_USER_PROJECT_ROLE = gql`
  mutation SetUserProjectRole($id: ID!, $roleId: ID!) {
    setUserProjectRole(id: $id, roleId: $roleId) {
      id
      role
    }
  }
`

export const CREATE_PROJECT_ROLE = gql`
  mutation CreateProjectRole($name: String!, $copyFromRoleId: ID) {
    createProjectRole(name: $name, copyFromRoleId: $copyFromRoleId) {
      id
      name
    }
  }
`

export const UPDATE_PROJECT_ROLE = gql`
  mutation UpdateProjectRole($id: ID!, $name: String!) {
    updateProjectRole(id: $id, name: $name) {
      id
      name
    }
  }
`

export const DELETE_PROJECT_ROLE = gql`
  mutation DeleteProjectRole($id: ID!, $reassignToRoleId: ID!) {
    deleteProjectRole(id: $id, reassignToRoleId: $reassignToRoleId)
  }
`

export const SET_ROLE_PERMISSIONS = gql`
  mutation SetRolePermissions($roleId: ID!, $permissions: [RolePermissionInput!]!) {
    setRolePermissions(roleId: $roleId, permissions: $permissions) {
      id
      permissions {
        module
        actions
      }
    }
  }
`

// ==================== SUBMITTALS ====================

export const CREATE_SUBMITTAL = gql`
  mutation createSubmittal($input: SubmittalInput!) {
    createSubmittal(input: $input) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const UPDATE_SUBMITTAL = gql`
  mutation updateSubmittal($id: ID!, $input: SubmittalInput!) {
    updateSubmittal(id: $id, input: $input) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const DELETE_SUBMITTAL = gql`
  mutation deleteSubmittal($id: ID!) {
    deleteSubmittal(id: $id)
  }
`

export const SUBMIT_SUBMITTAL_FOR_REVIEW = gql`
  mutation submitSubmittalForReview($id: ID!, $reviewerIds: [ID!]!, $message: String, $steps: [SubmittalWorkflowStepInput!], $templateId: ID) {
    submitSubmittalForReview(id: $id, reviewerIds: $reviewerIds, message: $message, steps: $steps, templateId: $templateId) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const UPSERT_SUBMITTAL_WORKFLOW_TEMPLATE = gql`
  mutation upsertSubmittalWorkflowTemplate($input: SubmittalWorkflowTemplateInput!) {
    upsertSubmittalWorkflowTemplate(input: $input) {
      ...SubmittalWorkflowTemplateFragment
    }
  }
  ${Fragments.SubmittalWorkflowTemplateFragment}
`

export const ARCHIVE_SUBMITTAL_WORKFLOW_TEMPLATE = gql`
  mutation archiveSubmittalWorkflowTemplate($id: ID!) {
    archiveSubmittalWorkflowTemplate(id: $id) {
      id
      archived
    }
  }
`

// Returns SubmittalReview!, not Submittal! — see src/graphql/submittals/lifecycle.ts.
// The brief's blanket "each lifecycle mutation returns the detail fragment" doesn't
// apply here; the real resolver return type only carries the single review row.
export const SET_SUBMITTAL_REVIEW_VERDICT = gql`
  mutation setSubmittalReviewVerdict($reviewId: ID!, $verdict: SubmittalVerdict!, $comment: String) {
    setSubmittalReviewVerdict(reviewId: $reviewId, verdict: $verdict, comment: $comment) {
      id
      reviewerId
      reviewer { id name }
      verdict
      comment
      decidedAt
    }
  }
`

export const CONSOLIDATE_SUBMITTAL = gql`
  mutation consolidateSubmittal($id: ID!, $verdict: SubmittalVerdict!, $note: String) {
    consolidateSubmittal(id: $id, verdict: $verdict, note: $note) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const WITHDRAW_SUBMITTAL = gql`
  mutation withdrawSubmittal($id: ID!) {
    withdrawSubmittal(id: $id) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const CREATE_SUBMITTAL_REVISION = gql`
  mutation createSubmittalRevision($id: ID!) {
    createSubmittalRevision(id: $id) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const CLOSE_SUBMITTAL = gql`
  mutation closeSubmittal($id: ID!) {
    closeSubmittal(id: $id) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const REOPEN_SUBMITTAL = gql`
  mutation reopenSubmittal($id: ID!, $mode: SubmittalReopenMode!, $reason: String!) {
    reopenSubmittal(id: $id, mode: $mode, reason: $reason) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const VOID_SUBMITTAL = gql`
  mutation voidSubmittal($id: ID!) {
    voidSubmittal(id: $id) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const PUBLISH_SUBMITTAL_TO_DOC_CONTROL = gql`
  mutation publishSubmittalToDocControl($id: ID!, $folderId: ID!) {
    publishSubmittalToDocControl(id: $id, folderId: $folderId) {
      ...SubmittalDetailFragment
    }
  }
  ${Fragments.SubmittalDetailFragment}
`

export const ADD_SUBMITTAL_ATTACHMENT = gql`
  mutation addSubmittalAttachment($revisionId: ID!, $fileId: ID, $docRevisionId: ID) {
    addSubmittalAttachment(revisionId: $revisionId, fileId: $fileId, docRevisionId: $docRevisionId) {
      id
      fileId
      file { id filename url size }
      docRevisionId
      docRevision { id documentId version file { id filename url } }
      addedBy { id name }
      createdAt
    }
  }
`

export const REMOVE_SUBMITTAL_ATTACHMENT = gql`
  mutation removeSubmittalAttachment($id: ID!) {
    removeSubmittalAttachment(id: $id)
  }
`

export const UPSERT_SUBMITTAL_LOOKUP_VALUE = gql`
  mutation upsertSubmittalLookupValue($projectId: ID!, $kind: SubmittalLookupKind!, $value: String!, $id: ID, $sortOrder: Int) {
    upsertSubmittalLookupValue(projectId: $projectId, kind: $kind, value: $value, id: $id, sortOrder: $sortOrder) {
      id
      kind
      value
      sortOrder
      archived
    }
  }
`

export const ARCHIVE_SUBMITTAL_LOOKUP_VALUE = gql`
  mutation archiveSubmittalLookupValue($id: ID!, $archived: Boolean!) {
    archiveSubmittalLookupValue(id: $id, archived: $archived) {
      id
      kind
      value
      sortOrder
      archived
    }
  }
`


// ==================== SCHEDULE ====================
export const CREATE_SCHEDULE = gql`
  mutation createSchedule($projectId: ID!, $input: ScheduleInput!) {
    createSchedule(projectId: $projectId, input: $input) {
      ...ScheduleFragment
    }
  }
  ${Fragments.ScheduleFragment}
`
export const UPDATE_SCHEDULE = gql`
  mutation updateSchedule($id: ID!, $input: ScheduleUpdateInput!) {
    updateSchedule(id: $id, input: $input) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const DELETE_SCHEDULE = gql`
  mutation deleteSchedule($id: ID!) {
    deleteSchedule(id: $id)
  }
`
export const CREATE_SCHEDULE_ACTIVITY = gql`
  mutation createScheduleActivity($scheduleId: ID!, $input: ScheduleActivityInput!) {
    createScheduleActivity(scheduleId: $scheduleId, input: $input) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const UPDATE_SCHEDULE_ACTIVITIES = gql`
  mutation updateScheduleActivities($scheduleId: ID!, $patches: [ScheduleActivityPatch!]!) {
    updateScheduleActivities(scheduleId: $scheduleId, patches: $patches) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const MOVE_SCHEDULE_ACTIVITY = gql`
  mutation moveScheduleActivity($scheduleId: ID!, $id: ID!, $parentId: ID, $index: Int!) {
    moveScheduleActivity(scheduleId: $scheduleId, id: $id, parentId: $parentId, index: $index) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const DELETE_SCHEDULE_ACTIVITIES = gql`
  mutation deleteScheduleActivities($scheduleId: ID!, $ids: [ID!]!) {
    deleteScheduleActivities(scheduleId: $scheduleId, ids: $ids) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const SET_SCHEDULE_ACTIVITY_PROGRESS = gql`
  mutation setScheduleActivityProgress($scheduleId: ID!, $patches: [ScheduleProgressPatch!]!) {
    setScheduleActivityProgress(scheduleId: $scheduleId, patches: $patches) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const CREATE_SCHEDULE_DEPENDENCY = gql`
  mutation createScheduleDependency($scheduleId: ID!, $input: ScheduleDependencyInput!) {
    createScheduleDependency(scheduleId: $scheduleId, input: $input) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const UPDATE_SCHEDULE_DEPENDENCY = gql`
  mutation updateScheduleDependency($scheduleId: ID!, $id: ID!, $type: ScheduleDependencyType, $lagDays: Int) {
    updateScheduleDependency(scheduleId: $scheduleId, id: $id, type: $type, lagDays: $lagDays) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const DELETE_SCHEDULE_DEPENDENCY = gql`
  mutation deleteScheduleDependency($scheduleId: ID!, $id: ID!) {
    deleteScheduleDependency(scheduleId: $scheduleId, id: $id) {
      ...ScheduleMutationResultFragment
    }
  }
  ${Fragments.ScheduleMutationResultFragment}
`
export const UPSERT_SCHEDULE_CALENDAR = gql`
  mutation upsertScheduleCalendar($projectId: ID!, $input: ScheduleCalendarInput!) {
    upsertScheduleCalendar(projectId: $projectId, input: $input) {
      ...ScheduleCalendarFragment
    }
  }
  ${Fragments.ScheduleCalendarFragment}
`
export const ARCHIVE_SCHEDULE_CALENDAR = gql`
  mutation archiveScheduleCalendar($projectId: ID!, $id: ID!) {
    archiveScheduleCalendar(projectId: $projectId, id: $id)
  }
`
export const CREATE_SCHEDULE_BASELINE = gql`
  mutation createScheduleBaseline($scheduleId: ID!, $name: String!) {
    createScheduleBaseline(scheduleId: $scheduleId, name: $name) {
      ...ScheduleBaselineFragment
    }
  }
  ${Fragments.ScheduleBaselineFragment}
`
export const DELETE_SCHEDULE_BASELINE = gql`
  mutation deleteScheduleBaseline($scheduleId: ID!, $id: ID!) {
    deleteScheduleBaseline(scheduleId: $scheduleId, id: $id)
  }
`
export const SET_SCHEDULE_ACTIVE_BASELINE = gql`
  mutation setScheduleActiveBaseline($scheduleId: ID!, $baselineId: ID) {
    setScheduleActiveBaseline(scheduleId: $scheduleId, baselineId: $baselineId) {
      ...ScheduleFragment
    }
  }
  ${Fragments.ScheduleFragment}
`
export const IMPORT_SCHEDULE_XML = gql`
  mutation importScheduleXml($projectId: ID!, $fileId: ID!, $name: String) {
    importScheduleXml(projectId: $projectId, fileId: $fileId, name: $name) {
      schedule {
        ...ScheduleFragment
      }
      activityCount
      dependencyCount
      calendarCount
      warnings {
        code
        message
        activityExternalUid
        activityName
      }
    }
  }
  ${Fragments.ScheduleFragment}
`
export const UPDATE_SCHEDULE_SETTINGS = gql`
  mutation updateScheduleSettings($projectId: ID!, $input: ScheduleSettingsInput!) {
    updateScheduleSettings(projectId: $projectId, input: $input) {
      projectId
      slipThresholdDays
    }
  }
`

export const SUBMIT_FEEDBACK = gql`
  mutation submitFeedback($input: SubmitFeedbackInput!) {
    submitFeedback(input: $input) {
      ok
      taskId
      taskUrl
    }
  }
`

export const CREATE_COST_NODE = gql`
  mutation createCostNode($projectId: ID!, $input: CostNodeCreateInput!) {
    createCostNode(projectId: $projectId, input: $input) {
      id
      parentId
      sortIndex
      kind
      name
      code
      buildingId
      originalBudget
      forecastOverride
    }
  }
`

export const UPDATE_COST_NODE = gql`
  mutation updateCostNode($id: ID!, $input: CostNodeUpdateInput!) {
    updateCostNode(id: $id, input: $input) {
      id
      parentId
      sortIndex
      kind
      name
      code
      buildingId
      originalBudget
      forecastOverride
      estimate
    }
  }
`

export const SET_COST_GROUP_ESTIMATE = gql`
  mutation setCostGroupEstimate($nodeId: ID!, $amount: Money) {
    setCostGroupEstimate(nodeId: $nodeId, amount: $amount) {
      id
      estimate
    }
  }
`

export const MOVE_COST_NODE = gql`
  mutation moveCostNode($id: ID!, $parentId: ID, $index: Int!) {
    moveCostNode(id: $id, parentId: $parentId, index: $index) {
      id
      parentId
      sortIndex
    }
  }
`

export const DELETE_COST_NODE = gql`
  mutation deleteCostNode($id: ID!) {
    deleteCostNode(id: $id)
  }
`

export const UPDATE_COST_CONTROL_SETTINGS = gql`
  mutation updateCostControlSettings($projectId: ID!, $input: CostControlSettingsInput!) {
    updateCostControlSettings(projectId: $projectId, input: $input) {
      ...CostControlSettingsFragment
    }
  }
  ${Fragments.CostControlSettingsFragment}
`

export const LOCK_COST_BUDGET = gql`
  mutation lockCostBudget($projectId: ID!) {
    lockCostBudget(projectId: $projectId) {
      ...CostControlSettingsFragment
    }
  }
  ${Fragments.CostControlSettingsFragment}
`

export const UNLOCK_COST_BUDGET = gql`
  mutation unlockCostBudget($projectId: ID!) {
    unlockCostBudget(projectId: $projectId) {
      ...CostControlSettingsFragment
    }
  }
  ${Fragments.CostControlSettingsFragment}
`

// Freelo #33146759: reads only; the price editor adds the rows to its draft.
export const READ_COST_PRICE_ITEMS_FILE = gql`
  mutation readCostPriceItemsFile($id: ID!, $fileId: ID!) {
    readCostPriceItemsFile(id: $id, fileId: $fileId) {
      items {
        rowNumber
        section
        code
        name
        unit
        quantity
        amount
        discountPercent
      }
      errors {
        rowNumber
        column
        key
        message
      }
    }
  }
`

export const IMPORT_COST_BUDGET = gql`
  mutation importCostBudget($projectId: ID!, $fileId: ID, $docRevisionId: ID, $dryRun: Boolean!) {
    importCostBudget(projectId: $projectId, fileId: $fileId, docRevisionId: $docRevisionId, dryRun: $dryRun) {
      applied
      groupsCreated
      itemsCreated
      itemsUpdated
      unchanged
      errors {
        rowNumber
        column
        key
        message
      }
    }
  }
`

export const CREATE_COST_CONTRACT = gql`
  mutation createCostContract($projectId: ID!, $input: CostContractCreateInput!) {
    createCostContract(projectId: $projectId, input: $input) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const UPDATE_COST_CONTRACT = gql`
  mutation updateCostContract($id: ID!, $input: CostContractUpdateInput!) {
    updateCostContract(id: $id, input: $input) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const SET_COST_CONTRACT_LINES = gql`
  mutation setCostContractLines($id: ID!, $lines: [CostContractLineInput!]!) {
    setCostContractLines(id: $id, lines: $lines) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const SET_COST_CONTRACT_PRICE_ITEMS = gql`
  mutation setCostContractPriceItems($id: ID!, $items: [CostContractPriceItemInput!]!, $discount: CostContractDiscountInput) {
    setCostContractPriceItems(id: $id, items: $items, discount: $discount) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const IMPORT_COST_CONTRACT_PRICE_ITEMS_FROM_TENDER = gql`
  mutation importCostContractPriceItemsFromTender($id: ID!) {
    importCostContractPriceItemsFromTender(id: $id) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const SET_COST_CONTRACT_STATUS = gql`
  mutation setCostContractStatus($id: ID!, $status: CostContractStatus!, $from: CostContractStatus) {
    setCostContractStatus(id: $id, status: $status, from: $from) {
      ...CostContractFragment
    }
  }
  ${Fragments.CostContractFragment}
`

export const DELETE_COST_CONTRACT = gql`
  mutation deleteCostContract($id: ID!) {
    deleteCostContract(id: $id)
  }
`

export const ADD_COST_CONTRACT_REPRESENTATIVE = gql`
  mutation addCostContractRepresentative($contractId: ID!, $userId: ID!) {
    addCostContractRepresentative(contractId: $contractId, userId: $userId) { id userId name email createdAt createdByName }
  }
`

export const REMOVE_COST_CONTRACT_REPRESENTATIVE = gql`
  mutation removeCostContractRepresentative($id: ID!) {
    removeCostContractRepresentative(id: $id)
  }
`

export const CREATE_COST_CONTRACT_FROM_TENDER = gql`
  mutation createCostContractFromTender($projectId: ID!, $tenderId: ID!) {
    createCostContractFromTender(projectId: $projectId, tenderId: $tenderId) {
      contract {
        ...CostContractFragment
      }
      awardedAmount
      itemCount
    }
  }
  ${Fragments.CostContractFragment}
`

export const SET_TENDER_COST_NODE = gql`
  mutation setTenderCostNode($tenderId: ID!, $nodeId: ID) {
    setTenderCostNode(tenderId: $tenderId, nodeId: $nodeId) {
      canEdit
      node { id name path }
      revisedBudget
      committed
      currency
      bids { bidderId bidderName amount difference differencePercent }
    }
  }
`

export const ADD_COST_ATTACHMENT = gql`
  mutation addCostAttachment($target: CostAttachmentTargetInput!, $fileId: ID, $docRevisionId: ID) {
    addCostAttachment(target: $target, fileId: $fileId, docRevisionId: $docRevisionId) {
      ...CostAttachmentFragment
    }
  }
  ${Fragments.CostAttachmentFragment}
`

export const REMOVE_COST_ATTACHMENT = gql`
  mutation removeCostAttachment($id: ID!) {
    removeCostAttachment(id: $id)
  }
`

export const CREATE_COST_CHANGE = gql`
  mutation createCostChange($projectId: ID!, $input: CostChangeCreateInput!) {
    createCostChange(projectId: $projectId, input: $input) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const UPDATE_COST_CHANGE = gql`
  mutation updateCostChange($id: ID!, $input: CostChangeUpdateInput!) {
    updateCostChange(id: $id, input: $input) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const DELETE_COST_CHANGE = gql`
  mutation deleteCostChange($id: ID!) {
    deleteCostChange(id: $id)
  }
`

export const SET_COST_CHANGE_LINES = gql`
  mutation setCostChangeLines($id: ID!, $lines: [CostChangeLineInput!]!) {
    setCostChangeLines(id: $id, lines: $lines) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const SUBMIT_COST_CHANGE = gql`
  mutation submitCostChange($id: ID!) {
    submitCostChange(id: $id) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const APPROVE_COST_CHANGE = gql`
  mutation approveCostChange($id: ID!) {
    approveCostChange(id: $id) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const REJECT_COST_CHANGE = gql`
  mutation rejectCostChange($id: ID!, $note: String!) {
    rejectCostChange(id: $id, note: $note) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const UNAPPROVE_COST_CHANGE = gql`
  mutation unapproveCostChange($id: ID!) {
    unapproveCostChange(id: $id) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const REOPEN_COST_CHANGE = gql`
  mutation reopenCostChange($id: ID!) {
    reopenCostChange(id: $id) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const CONVERT_COST_POTENTIAL_CHANGE = gql`
  mutation convertCostPotentialChange($id: ID!, $kind: CostChangeKind!, $contractId: ID) {
    convertCostPotentialChange(id: $id, kind: $kind, contractId: $contractId) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const DISMISS_COST_POTENTIAL_CHANGE = gql`
  mutation dismissCostPotentialChange($id: ID!, $note: String!) {
    dismissCostPotentialChange(id: $id, note: $note) {
      ...CostChangeFragment
    }
  }
  ${Fragments.CostChangeFragment}
`

export const CREATE_COST_RFQ_FROM_POTENTIAL_CHANGE = gql`
  mutation createCostRfqFromPotentialChange($id: ID!) {
    createCostRfqFromPotentialChange(id: $id) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const CREATE_COST_INVOICE = gql`
  mutation createCostInvoice($projectId: ID!, $input: CostInvoiceCreateInput!) {
    createCostInvoice(projectId: $projectId, input: $input) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const UPDATE_COST_INVOICE = gql`
  mutation updateCostInvoice($id: ID!, $input: CostInvoiceUpdateInput!) {
    updateCostInvoice(id: $id, input: $input) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const DELETE_COST_INVOICE = gql`
  mutation deleteCostInvoice($id: ID!) {
    deleteCostInvoice(id: $id)
  }
`

export const SET_COST_INVOICE_LINES = gql`
  mutation setCostInvoiceLines($id: ID!, $lines: [CostInvoiceLineInput!]!) {
    setCostInvoiceLines(id: $id, lines: $lines) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const SET_COST_INVOICE_STATUS = gql`
  mutation setCostInvoiceStatus($id: ID!, $status: CostInvoiceStatus!, $from: CostInvoiceStatus, $paidAt: Date) {
    setCostInvoiceStatus(id: $id, status: $status, from: $from, paidAt: $paidAt) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const CREATE_COST_EXPENSE = gql`
  mutation createCostExpense($projectId: ID!, $input: CostExpenseCreateInput!) {
    createCostExpense(projectId: $projectId, input: $input) {
      ...CostExpenseFragment
    }
  }
  ${Fragments.CostExpenseFragment}
`

export const UPDATE_COST_EXPENSE = gql`
  mutation updateCostExpense($id: ID!, $input: CostExpenseUpdateInput!) {
    updateCostExpense(id: $id, input: $input) {
      ...CostExpenseFragment
    }
  }
  ${Fragments.CostExpenseFragment}
`

export const DELETE_COST_EXPENSE = gql`
  mutation deleteCostExpense($id: ID!) {
    deleteCostExpense(id: $id)
  }
`

export const CREATE_COST_APPLICATION = gql`
  mutation createCostApplication($contractId: ID!, $input: CostApplicationInput!) {
    createCostApplication(contractId: $contractId, input: $input) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const UPDATE_COST_APPLICATION = gql`
  mutation updateCostApplication($id: ID!, $input: CostApplicationInput!) {
    updateCostApplication(id: $id, input: $input) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const DELETE_COST_APPLICATION = gql`
  mutation deleteCostApplication($id: ID!) {
    deleteCostApplication(id: $id)
  }
`

export const SET_COST_APPLICATION_LINES = gql`
  mutation setCostApplicationLines($id: ID!, $lines: [CostApplicationLineInput!]!, $manualLines: [CostApplicationManualLineInput!]) {
    setCostApplicationLines(id: $id, lines: $lines, manualLines: $manualLines) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const SUBMIT_COST_APPLICATION = gql`
  mutation submitCostApplication($id: ID!) {
    submitCostApplication(id: $id) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const RETURN_COST_APPLICATION = gql`
  mutation returnCostApplication($id: ID!, $note: String!) {
    returnCostApplication(id: $id, note: $note) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const SAVE_COST_APPLICATION_REVIEW = gql`
  mutation saveCostApplicationReview($id: ID!, $lines: [CostApplicationReviewInput!]!, $manualRows: [CostApplicationManualReviewInput!]) {
    saveCostApplicationReview(id: $id, lines: $lines, manualRows: $manualRows) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const CERTIFY_COST_APPLICATION = gql`
  mutation certifyCostApplication($id: ID!, $lines: [CostApplicationReviewInput!]) {
    certifyCostApplication(id: $id, lines: $lines) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const UNCERTIFY_COST_APPLICATION = gql`
  mutation uncertifyCostApplication($id: ID!) {
    uncertifyCostApplication(id: $id) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const SET_COST_APPLICATION_INVOICE = gql`
  mutation setCostApplicationInvoice($applicationId: ID!, $input: CostApplicationInvoiceInput) {
    setCostApplicationInvoice(applicationId: $applicationId, input: $input) {
      ...CostApplicationFragment
    }
  }
  ${Fragments.CostApplicationFragment}
`

export const CREATE_COST_INVOICE_FROM_APPLICATION = gql`
  mutation createCostInvoiceFromApplication($applicationId: ID!, $input: CostInvoiceUpdateInput!) {
    createCostInvoiceFromApplication(applicationId: $applicationId, input: $input) {
      ...CostInvoiceFragment
    }
  }
  ${Fragments.CostInvoiceFragment}
`

export const ADD_COST_RETENTION_RELEASE = gql`
  mutation addCostRetentionRelease($contractId: ID!, $input: CostRetentionReleaseInput!) {
    addCostRetentionRelease(contractId: $contractId, input: $input) {
      id
    }
  }
`

export const DELETE_COST_RETENTION_RELEASE = gql`
  mutation deleteCostRetentionRelease($id: ID!) {
    deleteCostRetentionRelease(id: $id)
  }
`

export const CREATE_COST_RFQ = gql`
  mutation createCostRfq($projectId: ID!, $input: CostRfqCreateInput!) {
    createCostRfq(projectId: $projectId, input: $input) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const UPDATE_COST_RFQ = gql`
  mutation updateCostRfq($id: ID!, $input: CostRfqUpdateInput!) {
    updateCostRfq(id: $id, input: $input) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const DELETE_COST_RFQ = gql`
  mutation deleteCostRfq($id: ID!) {
    deleteCostRfq(id: $id)
  }
`

export const ADD_COST_RFQ_QUOTE = gql`
  mutation addCostRfqQuote($rfqId: ID!, $input: CostRfqQuoteInput!) {
    addCostRfqQuote(rfqId: $rfqId, input: $input) {
      id
    }
  }
`

export const UPDATE_COST_RFQ_QUOTE = gql`
  mutation updateCostRfqQuote($id: ID!, $input: CostRfqQuoteInput!) {
    updateCostRfqQuote(id: $id, input: $input) {
      id
    }
  }
`

export const DELETE_COST_RFQ_QUOTE = gql`
  mutation deleteCostRfqQuote($id: ID!) {
    deleteCostRfqQuote(id: $id)
  }
`

export const AWARD_COST_RFQ = gql`
  mutation awardCostRfq($quoteId: ID!) {
    awardCostRfq(quoteId: $quoteId) {
      rfq {
        ...CostRfqFragment
      }
      contractId
      changeId
    }
  }
  ${Fragments.CostRfqFragment}
`

export const REVOKE_COST_RFQ_AWARD = gql`
  mutation revokeCostRfqAward($rfqId: ID!) {
    revokeCostRfqAward(rfqId: $rfqId) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const CANCEL_COST_RFQ = gql`
  mutation cancelCostRfq($id: ID!) {
    cancelCostRfq(id: $id) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const REOPEN_COST_RFQ = gql`
  mutation reopenCostRfq($id: ID!) {
    reopenCostRfq(id: $id) {
      ...CostRfqFragment
    }
  }
  ${Fragments.CostRfqFragment}
`

export const LINK_COST_CONTRACT_SUBMITTALS = gql`
  mutation linkCostContractSubmittals($contractId: ID!, $submittalIds: [ID!]!) {
    linkCostContractSubmittals(contractId: $contractId, submittalIds: $submittalIds) { id }
  }
`
export const UNLINK_COST_CONTRACT_SUBMITTAL = gql`
  mutation unlinkCostContractSubmittal($contractId: ID!, $submittalId: ID!) {
    unlinkCostContractSubmittal(contractId: $contractId, submittalId: $submittalId) { id }
  }
`

export const SWITCH_COST_ATTACHMENT_TO_CURRENT = gql`
  mutation switchCostAttachmentToCurrent($id: ID!) {
    switchCostAttachmentToCurrent(id: $id) { ...CostAttachmentFragment }
  }
  ${Fragments.CostAttachmentFragment}
`
